// frontend/src/App.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useApi from './hooks/useApi';
import Sidebar from './components/Sidebar';
import AuthScreen from './components/AuthScreen';
import Dashboard from './pages/Dashboard';
import TransactionsPage from './pages/TransactionsPage';
import AccountsPage from './pages/AccountsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import GeminiAssistant from './pages/GeminiAssistant';
import LogsPage from './pages/LogsPage';
import PetClientsPage from './pages/PetClientsPage';
import PetSuppliersPage from './pages/PetSuppliersPage'; 
import GenericClientsPage from './pages/GenericClientsPage';
import CommissionsPage from './pages/CommissionsPage'; // NOVO: IMPORTAÇÃO DO MÓDULO DE COMISSÕES
import BackupPage from './pages/BackupPage';
import AvisosPage from './pages/AvisosPage';
import AvisoDisplay from './components/AvisoDisplay';
import UserAvisosPage from './pages/UserAvisosPage';
import ContasAPagarPage from './pages/ContasAPagarPage';
import ContasAReceberPage from './pages/ContasAReceberPage';
import LayoutSettingsPage from './pages/LayoutSettingsPage';
import ReportLayoutPage from './pages/ReportLayoutPage'; 
import OsNsaPage from './pages/OsNsaPage';
import ServiceTablePage from './pages/ServiceTablePage';
import PetServiceTablePage from './pages/PetServiceTablePage'; 
import SchedulePage from './pages/SchedulePage';
import ComercialStatusPage from './pages/ComercialStatusPage'; 
import MonitoringPage from './pages/MonitoringPage';
import LaudosVencimentoPage from './pages/LaudosVencimentoPage'; 

// IMPORTAÇÕES: ESTOQUE E PRODUTOS
import ProductClassificationsPage from './pages/ProductClassificationsPage';
import ProductsPage from './pages/ProductsPage';
import DepositsPage from './pages/DepositsPage'; 
import ConsultaEstoque from './pages/ConsultaEstoque'; 
import MovimentacaoDepositos from './pages/MovimentacaoDepositos';
import BaixasPage from './pages/BaixasPage'; 
import InventoryAuditPage from './pages/InventoryAuditPage'; 

// IMPORTAÇÕES: COMPRAS E NF
import PaymentConditionsPage from './pages/PaymentConditionsPage';
import SupplierNegotiationPage from './pages/SupplierNegotiationPage';
import PurchaseOrderPage from './pages/PurchaseOrderPage';
import InvoiceEntryPage from './pages/InvoiceEntryPage'; 

// IMPORTAÇÃO: MÓDULO DE FROTA E EQUIPES
import FleetFuelPage from './pages/FleetFuelPage';
import FleetTeamPage from './pages/FleetTeamPage'; // NOVO: IMPORTAÇÃO DO CADASTRO DE EQUIPES

import ChangePasswordModal from './components/modals/ChangePasswordModal';
import ChangeKeywordModal from './components/modals/ChangeKeywordModal';
import EditUserModal from './components/modals/EditUserModal';
import EditEntityModal from './components/modals/EditEntityModal';
import { io } from "socket.io-client";
import { Loader2 } from 'lucide-react'; 

const SOCKET_URL = window.location.origin;

// --- Placeholder Components ---
const PetAgenda = () => <div className="text-center"><h1 className="text-2xl font-bold">Agenda Pet</h1><p>Página em construção.</p></div>;
const EnergiaDashboard = () => <div className="text-center"><h1 className="text-2xl font-bold">Dashboard Energia</h1><p>Página em construção.</p></div>;
const EnergiaMedicoes = () => <div className="text-center"><h1 className="text-2xl font-bold">Medições de Energia</h1><p>Página em construção.</p></div>;


const formatCurrency = (value, currency = 'BRL') => {
    try {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: currency,
        }).format(value || 0);
    } catch (e) {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        }).format(value || 0);
    }
};

const App = () => {
    const { token, removeToken, saveToken, apiFetch, currentUser } = useApi();
    
    const [currentPage, setCurrentPage] = useState(null); 
    const [loadingData, setLoadingData] = useState(true);

    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [isChangeKeywordOpen, setIsChangeKeywordOpen] = useState(false);
    const [editingUserId, setEditingUserId] = useState(null);
    const [editingEntity, setEditingEntity] = useState(null);

    const [transactions, setTransactions] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [accountTypes, setAccountTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [settings, setSettings] = useState({});
    const [entities, setEntities] = useState([]);
    const [isSwitchingEntity, setIsSwitchingEntity] = useState(false);
    const [isEntityActive, setIsEntityActive] = useState(true);
    const [allCurrencies, setAllCurrencies] = useState([]);
    
    const [unreadCount, setUnreadCount] = useState(0);
    const [avisoSettings, setAvisoSettings] = useState({ playSound: true, showPopup: true });
    
    const [avisoRefreshTrigger, setAvisoRefreshTrigger] = useState(0);

    const [dataChangeKey, setDataChangeKey] = useState(0);
    const [selectedCurrency, setSelectedCurrency] = useState('BRL');

    const [calendarMonth, setCalendarMonth] = useState(new Date());
    const [selectedCalendarDate, setSelectedCalendarDate] = useState(new Date());
    const [selectedTransactionUserId, setSelectedTransactionUserId] = useState('');

    const [openNovoRecebimentoData, setOpenNovoRecebimentoData] = useState(null);

    const handleAvisoRefresh = useCallback(() => {
        setAvisoRefreshTrigger(prev => prev + 1);
    }, []);

    // --- LÓGICA DE REDIRECIONAMENTO ---
    const getStartPage = useCallback((user) => {
        if (!user) return 'dashboard';
        
        if (user.is_client) return 'os-nsa';

        const { permissions, is_super_user, business_type } = user;

        if (is_super_user || permissions?.dashboard) return 'dashboard';

        if (business_type === 'energia') {
            if (permissions?.comercial_status_view) return 'comercial-status';
            if (permissions?.comercial_schedule_view) return 'schedule';
            if (permissions?.comercial_os_view) return 'os-nsa';
            if (permissions?.laudos_vencidos_view) return 'laudos-vencimento'; 
        }

        if (business_type === 'pet') {
            if (permissions?.clients_view) return 'pet-clients';
            if (permissions?.service_table_view) return 'pet-service-table';
        }

        if (permissions?.products_view) return 'products';
        if (permissions?.product_classifications_view) return 'product-classifications';

        // ESTOQUE
        if (permissions?.deposits_view) return 'deposits';
        if (permissions?.inventory_consult_view) return 'inventory-consult';
        if (permissions?.stock_movements_view) return 'inventory-movements';
        if (permissions?.inventory_write_off_view) return 'inventory-write-off';
        
        if (permissions?.inventory_audit_view || permissions?.inventory_mobile_collect) return 'inventory-audit';

        // COMPRAS
        if (permissions?.payment_conditions_view) return 'payment-conditions';
        if (permissions?.supplier_negotiation_view) return 'supplier-negotiation';
        if (permissions?.purchase_order_view) return 'purchase-order';
        if (permissions?.invoice_entry_view) return 'invoice-entry'; 

        // Páginas Administrativas/Financeiras
        if (permissions?.transactions_view) return 'transactions';
        if (permissions?.payables_view) return 'contas-a-pagar';
        if (permissions?.receivables_view) return 'contas-a-receber';
        if (permissions?.avisos_view) return 'avisos';
        if (permissions?.monitoring_view) return 'monitoring'; 

        if (
            permissions?.reports_view || 
            permissions?.reports ||
            permissions?.report_cash_book ||
            permissions?.report_ledger ||
            permissions?.report_payables ||
            permissions?.report_receivables ||
            permissions?.report_dre ||
            permissions?.report_services_client
        ) {
            return 'reports';
        }

        return 'meus-avisos';
    }, []);

    useEffect(() => {
        if (currentUser && !loadingData) {
            if (!currentPage || currentPage === 'dashboard') {
                const targetPage = getStartPage(currentUser);
                if (targetPage !== currentPage) {
                    console.log(`[Rota] Redirecionando ${currentUser.username} para: ${targetPage}`);
                    setCurrentPage(targetPage);
                }
            }
        }
    }, [currentUser, loadingData, currentPage, getStartPage]);

    const fetchAvisoSettings = useCallback(async () => {
        if (currentUser?.is_client) return;

        try {
            const userPrefs = await apiFetch('/avisos/settings');
            let checkInterval = 60; 
            
            if (currentUser && (currentUser.is_super_user || currentUser.permissions?.settings_view)) {
                try {
                    const globalSettings = await apiFetch('/settings');
                    if (globalSettings && globalSettings.aviso_check_interval) {
                        checkInterval = globalSettings.aviso_check_interval;
                    }
                } catch (err) {}
            }

            setAvisoSettings({
                ...userPrefs,
                aviso_check_interval: checkInterval
            });

        } catch (err) {
            console.error("Erro ao buscar configurações de aviso:", err.message);
        }
    }, [apiFetch, currentUser]);

    const fetchData = useCallback(async (filterUserId = '') => {
        if (!currentUser) return;
        try {
            if (!currentUser.is_client) {
                fetchAvisoSettings();
                handleAvisoRefresh();
            }

            const params = new URLSearchParams();
            if (filterUserId) {
                params.append('userId', filterUserId);
            }
            const transactionUrl = '/transactions' + (params.toString() ? `?${params.toString()}` : '');

            const promises = [];

            if (currentUser.is_super_user || currentUser.permissions?.transactions_view) {
                promises.push(apiFetch(transactionUrl).then(data => ({ type: 'transactions', data })));
            }

            if (currentUser.is_super_user || currentUser.permissions?.accounts_view) {
                promises.push(apiFetch('/accounts').then(data => ({ type: 'accounts', data })));
            }

            if (currentUser.is_super_user || currentUser.permissions?.settings_view) {
                promises.push(apiFetch('/settings').then(data => ({ type: 'settings', data })));
            }

            promises.push(apiFetch('/account-types').then(data => ({ type: 'accountTypes', data })));
            promises.push(apiFetch('/categories').then(data => ({ type: 'categories', data })));
            promises.push(apiFetch('/currencies').then(data => ({ type: 'currencies', data })));
            promises.push(apiFetch('/entities').then(data => ({ type: 'entities', data })));

            const results = await Promise.allSettled(promises);

            results.forEach(result => {
                if (result.status === 'fulfilled') {
                    const { type, data } = result.value;
                    switch (type) {
                        case 'transactions': setTransactions(data || []); break;
                        case 'accounts': setAccounts(data || []); break;
                        case 'settings': setSettings(data || {}); break;
                        case 'accountTypes': setAccountTypes(data || []); break;
                        case 'categories': setCategories(data || []); break;
                        case 'currencies': setAllCurrencies(data || []); break;
                        case 'entities':
                            const allEntities = data || [];
                            setEntities(allEntities);
                            const currentEntity = allEntities.find(e => e.id === currentUser.entityId);
                            setIsEntityActive(currentEntity ? currentEntity.status === 'ativo' : true);
                            break;
                        default: break;
                    }
                }
            });

        } catch (error) {
            console.error("Falha inesperada ao processar dados:", error);
        } finally {
            setLoadingData(false); 
            setDataChangeKey(prev => prev + 1);
        }
    }, [apiFetch, currentUser, fetchAvisoSettings, handleAvisoRefresh]);

    const checkFinancialAlerts = useCallback(async () => {
        if (!currentUser || currentUser.is_client) return; 
        try {
            console.log("Verificando alertas financeiros...");
            const response = await apiFetch('/avisos/check-financial', { method: 'POST' });
            console.log(response.message);
            handleAvisoRefresh();
        } catch (error) {
            console.error("Erro ao verificar alertas financeiros:", error);
        }
    }, [apiFetch, currentUser, handleAvisoRefresh]);

    useEffect(() => {
        if (token && currentUser) {
            setSelectedCurrency(currentUser.defaultCurrency || (currentUser.allowedCurrencies && currentUser.allowedCurrencies[0]) || 'BRL');

            fetchAvisoSettings();
            fetchData(selectedTransactionUserId);
            checkFinancialAlerts();

            const socket = io(SOCKET_URL);

            socket.on('connect', () => {
                const entityRoom = `entity_${currentUser.entityIdentifier}`;
                socket.emit('join_entity_room', entityRoom);
            });

            socket.on('data_updated', () => {
                console.log('Recebido evento data_updated. A recarregar todos os dados.');
                fetchData(selectedTransactionUserId);
            });

            return () => {
                socket.disconnect();
            };

        } else if (!token) {
            setLoadingData(false);
        }
    }, [token, currentUser, fetchData, selectedTransactionUserId, checkFinancialAlerts, fetchAvisoSettings]);

    // --- NOVO EFEITO: PING DE USUÁRIO ONLINE E CAPTURA DE FECHAMENTO DE TELA ---
    useEffect(() => {
        if (!token || !currentUser) return;

        const sendPing = async () => {
            try {
                await apiFetch('/monitor/web-ping', { method: 'POST' });
            } catch (err) {
                console.error("Erro ao enviar ping de usuário online:", err);
            }
        };

        sendPing(); // Dispara imediatamente após logar
        const pingInterval = setInterval(sendPing, 30000); // Repete a cada 30 segundos

        // Detecta quando o usuário simplesmente fecha o navegador no "X"
        const handleTabClose = () => {
            // O keepalive: true força a entrega da requisição HTTP mesmo que a aba seja destruída
            const apiUrl = window.location.hostname.includes('localhost') ? 'http://localhost:8080/api' : '/api';
            fetch(`${apiUrl}/monitor/web-logout`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                keepalive: true
            }).catch(() => {});
        };

        window.addEventListener('beforeunload', handleTabClose);

        return () => {
            clearInterval(pingInterval);
            window.removeEventListener('beforeunload', handleTabClose);
        };
    }, [token, currentUser, apiFetch]);

    const handleLogin = (newToken) => {
        saveToken(newToken);
        setLoadingData(true); 
    };

    const handleLogout = async () => {
        // Avisa o backend instantaneamente antes de deslogar
        try {
            if (token && currentUser) {
                await apiFetch('/monitor/web-logout', { method: 'POST' });
            }
        } catch (e) {
            console.error("Erro ao avisar logout ao servidor:", e);
        }

        removeToken();
        setTransactions([]);
        setAccounts([]);
        setAccountTypes([]);
        setSettings({});
        setCategories([]);
        setCurrentPage(null); 
    };

    const handleSwitchEntity = async (entityId) => {
        if (entityId === currentUser.entityId) return;

        setIsSwitchingEntity(true);
        try {
            const data = await apiFetch('/admin/switch-entity', {
                method: 'POST',
                body: { entityId },
            });
            saveToken(data.accessToken);
            window.location.reload(); 
        } catch (error) {
            alert(`Erro ao mudar de entidade: ${error.message}`);
        } finally {
            setIsSwitchingEntity(false);
        }
    };

    const handleUpdateAvisoSettings = async (key, newValue) => {
        const newSettings = { ...avisoSettings, [key]: newValue };
        setAvisoSettings(newSettings); 
        try {
            await apiFetch('/avisos/settings', {
                method: 'PUT',
                body: { [key]: newValue } 
            });
            handleAvisoRefresh();
        } catch (err) {
            console.error("Erro ao salvar configuração de aviso:", err.message);
            setAvisoSettings(prev => ({ ...prev, [key]: !newValue }));
            alert(`Erro ao salvar configuração: ${err.message}`);
        }
    };

    const filteredData = useMemo(() => {
        const accountsFiltered = accounts.filter(a => a.currency === selectedCurrency);
        const accountIdsFiltered = new Set(accountsFiltered.map(a => a.id));
        const transactionsFiltered = transactions.filter(t =>
            accountIdsFiltered.has(t.account_id) || accountIdsFiltered.has(t.to_account_id)
        );
        return { accounts: accountsFiltered, transactions: transactionsFiltered };
    }, [accounts, transactions, selectedCurrency]);

    const handleNavigateWithData = (page, data) => {
        if (page === 'contas-a-receber') {
            setOpenNovoRecebimentoData(data);
        }
        setCurrentPage(page);
    };

    if (!token) {
        return <AuthScreen onLogin={handleLogin} />;
    }

    if (loadingData || !currentUser || !currentPage) {
        return <div className="flex items-center justify-center w-full h-screen"><Loader2 className="animate-spin mr-2" /> <p>A carregar aplicação...</p></div>;
    }

    const { permissions, is_super_user } = currentUser;

    const renderPage = () => {
        switch (currentPage) {
            case 'dashboard': 
                if (!is_super_user && !permissions?.dashboard) return <div className="text-center p-10">Acesso não autorizado. Redirecionando...</div>;
                return <Dashboard {...commonProps} transactions={filteredData.transactions} accounts={filteredData.accounts} accountTypes={accountTypes} />;
            
            case 'transactions': return <TransactionsPage
                                                {...commonProps}
                                                transactions={filteredData.transactions}
                                                accounts={accounts} 
                                                categories={categories}
                                                accountTypes={accountTypes}
                                                currentMonth={calendarMonth}
                                                setCurrentMonth={setCalendarMonth}
                                                selectedDate={selectedCalendarDate}
                                                setSelectedDate={setSelectedCalendarDate}
                                                selectedUserId={selectedTransactionUserId}
                                                onUserFilterChange={setSelectedTransactionUserId}
                                                isEntityActive={isEntityActive}
                                                onDataChange={() => fetchData(selectedTransactionUserId)}
                                                 />;
            case 'accounts': return <AccountsPage
                                                {...commonProps}
                                                accounts={accounts} 
                                                accountTypes={accountTypes}
                                                onDataChange={() => fetchData(selectedTransactionUserId)}
                                                 />;
            case 'reports': return <ReportsPage {...commonProps} accounts={accounts} />;
            case 'settings': return <SettingsPage
                                                {...commonProps}
                                                accountTypes={accountTypes}
                                                onDataChange={() => fetchData(selectedTransactionUserId)}
                                                settings={settings}
                                                permissions={permissions}
                                                onEditUser={setEditingUserId}
                                                onEditEntity={setEditingEntity}
                                                 />;
            case 'assistant': return <GeminiAssistant {...commonProps} transactions={transactions} accounts={accounts} />;
            case 'systemLogs': return <LogsPage {...commonProps} />;
            
            case 'monitoring':
                if (!is_super_user && !permissions?.monitoring_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <MonitoringPage {...commonProps} />;

            case 'pet-clients': return <PetClientsPage
                                                {...commonProps}
                                                onDataChange={() => fetchData(selectedTransactionUserId)}
                                                 />;
            
            case 'pet-suppliers': return <PetSuppliersPage
                                                {...commonProps}
                                                onDataChange={() => fetchData(selectedTransactionUserId)}
                                                 />;

            case 'generic-clients': return <GenericClientsPage
                                                {...commonProps}
                                                onDataChange={() => fetchData(selectedTransactionUserId)}
                                                 />;

            case 'commissions': // NOVO: Rota para Comissões
                if (!is_super_user && !permissions?.commissions_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <CommissionsPage {...commonProps} />;
            
            case 'service-table': return <ServiceTablePage {...commonProps} />;

            case 'pet-service-table': return <PetServiceTablePage 
                                                {...commonProps}
                                                selectedCurrency={selectedCurrency} 
                                                formatCurrency={formatCurrency}
                                                allCurrencies={allCurrencies}
                                                />;
            
            case 'schedule': return <SchedulePage 
                                                   {...commonProps} 
                                                   dataChangeKey={dataChangeKey} 
                                                 />;

            case 'comercial-status': return <ComercialStatusPage {...commonProps} />;

            case 'contas-a-pagar': return <ContasAPagarPage
                                                    {...commonProps}
                                                    accounts={accounts} 
                                                    accountTypes={accountTypes}
                                                    onDataChange={() => fetchData(selectedTransactionUserId)}
                                                    dataChangeKey={dataChangeKey}
                                                    isEntityActive={isEntityActive}
                                                     />;
            
            case 'contas-a-receber': return <ContasAReceberPage
                                                    {...commonProps}
                                                    accounts={accounts} 
                                                    accountTypes={accountTypes}
                                                    onDataChange={() => fetchData(selectedTransactionUserId)}
                                                    dataChangeKey={dataChangeKey}
                                                    isEntityActive={isEntityActive}
                                                    openNovoRecebimentoData={openNovoRecebimentoData}
                                                     />;
            
            case 'os-nsa': return <OsNsaPage
                                                    {...commonProps}
                                                    onDataChange={() => fetchData(selectedTransactionUserId)}
                                                    onNavigate={handleNavigateWithData} 
                                                    dataChangeKey={dataChangeKey}
                                                     />;

            case 'layout-settings': 
                if (!is_super_user && !permissions?.layout_docs_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <LayoutSettingsPage
                                                    {...commonProps}
                                                    onDataChange={() => fetchData(selectedTransactionUserId)}
                                                     />;

            case 'report-layout': 
                if (!is_super_user && !permissions?.layout_laudos_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <ReportLayoutPage
                                                    {...commonProps}
                                                    onDataChange={() => fetchData(selectedTransactionUserId)}
                                                     />;

            case 'backup': 
                if (!is_super_user && !permissions?.backup_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <BackupPage {...commonProps} />;
            
            case 'avisos': return <AvisosPage {...commonProps} />;

            case 'meus-avisos': return <UserAvisosPage 
                                                    {...commonProps} 
                                                    avisoSettings={avisoSettings}
                                                    onUpdateSettings={handleUpdateAvisoSettings}
                                                    onAvisoRefresh={handleAvisoRefresh}
                                                     />;

            case 'laudos-vencimento': 
                if (!is_super_user && !permissions?.laudos_vencidos_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <LaudosVencimentoPage {...commonProps} />;

            case 'product-classifications':
                if (!is_super_user && !permissions?.product_classifications_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <ProductClassificationsPage {...commonProps} onDataChange={() => fetchData(selectedTransactionUserId)} />;

            case 'products':
                if (!is_super_user && !permissions?.products_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <ProductsPage {...commonProps} onDataChange={() => fetchData(selectedTransactionUserId)} />;

            case 'deposits': 
                if (!is_super_user && !permissions?.deposits_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <DepositsPage {...commonProps} dataChangeKey={dataChangeKey} />;

            case 'inventory-consult':
                if (!is_super_user && !permissions?.inventory_consult_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <ConsultaEstoque {...commonProps} />; 

            case 'inventory-movements':
                if (!is_super_user && !permissions?.stock_movements_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <MovimentacaoDepositos {...commonProps} />;

            case 'inventory-write-off':
                if (!is_super_user && !permissions?.inventory_write_off_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <BaixasPage {...commonProps} />; 

            case 'inventory-audit':
                if (!is_super_user && !permissions?.inventory_audit_view && !permissions?.inventory_mobile_collect) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <InventoryAuditPage {...commonProps} />;

            // --- CASOS DO MÓDULO DE COMPRAS ---
            case 'payment-conditions':
                if (!is_super_user && !permissions?.payment_conditions_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <PaymentConditionsPage {...commonProps} />;

            case 'supplier-negotiation':
                if (!is_super_user && !permissions?.supplier_negotiation_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <SupplierNegotiationPage {...commonProps} />;

            case 'purchase-order':
                if (!is_super_user && !permissions?.purchase_order_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <PurchaseOrderPage {...commonProps} />;
            
            // --- NOVO CASO PARA ENTRADA DE NF E CONTROLE DE FROTA ---
            case 'invoice-entry':
                if (!is_super_user && !permissions?.invoice_entry_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <InvoiceEntryPage {...commonProps} />; 
            
            // --- NOVO MÓDULO DE FROTA E EQUIPES ---
            case 'fleet-fuel':
                if (!is_super_user && !permissions?.fleet_fuel_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <FleetFuelPage {...commonProps} />; 

            case 'fleet-team':
                if (!is_super_user && !permissions?.fleet_team_view) return <div className="text-center p-10">Acesso não autorizado.</div>;
                return <FleetTeamPage {...commonProps} />; 

            case 'pet-agenda': return <PetAgenda />;
            case 'energia-dashboard': return <EnergiaDashboard />;
            case 'energia-medicoes': return <EnergiaMedicoes />;

            default:
                return (
                    <div className="text-center">
                        <h1 className="text-2xl font-bold text-red-600">Página não encontrada ou Acesso Negado</h1>
                        <p>Tente navegar usando o menu lateral.</p>
                    </div>
                );
        }
    };

    const commonProps = {
        currentUser,
        apiFetch,
        selectedCurrency,
        formatCurrency,
        allCurrencies
    };

    return (
        <div className="flex flex-col md:flex-row h-screen bg-gray-100 font-inter">
            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                onLogout={handleLogout}
                permissions={permissions}
                currentUser={currentUser}
                onOpenChangePasswordModal={() => setIsChangePasswordOpen(true)}
                onOpenChangeKeywordModal={() => setIsChangeKeywordOpen(true)}
                onSwitchEntity={handleSwitchEntity}
                entities={entities}
                isSwitchingEntity={isSwitchingEntity}
                selectedCurrency={selectedCurrency}
                setSelectedCurrency={setSelectedCurrency}
                allCurrencies={allCurrencies}
                unreadCount={unreadCount} 
            />
            <main className="flex-1 overflow-y-auto p-4 pt-20 pb-24 md:p-8 md:pt-8 md:pb-8">
                {!isEntityActive && (
                    <div className="p-4 mb-4 text-orange-800 bg-orange-100 border-l-4 border-orange-500 rounded-r-lg" role="alert">
                        <p className="font-bold">Entidade Inativa</p>
                        <p>Esta entidade está atualmente inativa. Apenas a visualização de dados é permitida. Para fazer novos lançamentos, por favor, ative a entidade nas configurações.</p>
                    </div>
                )}
                {renderPage()}
            </main>

            {!currentUser.is_client && (
                <AvisoDisplay 
                    onUnreadCountChange={setUnreadCount} 
                    avisoSettings={avisoSettings}
                    refreshTrigger={avisoRefreshTrigger} 
                />
            )}

            {isChangePasswordOpen && (
                <ChangePasswordModal
                    onClose={() => setIsChangePasswordOpen(false)}
                    onSuccess={() => {
                        alert('Senha alterada com sucesso!');
                        setIsChangePasswordOpen(false);
                    }}
                />
            )}
             {isChangeKeywordOpen && (
                <ChangeKeywordModal
                    onClose={() => setIsChangeKeywordOpen(false)}
                    onSuccess={() => {
                        alert('Palavra-chave alterada com sucesso!');
                        setIsChangeKeywordOpen(false);
                    }}
                />
            )}
             {editingUserId && (
                <EditUserModal
                    userId={editingUserId}
                    onClose={() => setEditingUserId(null)}
                    onUserUpdated={() => {
                        setEditingUserId(null);
                        fetchData();
                    }}
                    allCurrencies={allCurrencies}
                    currentUser={currentUser}
                />
            )}
            {editingEntity && (
                <EditEntityModal
                    entity={editingEntity}
                    onClose={() => setEditingEntity(null)}
                    onEntityUpdated={() => {
                        setEditingEntity(null);
                        fetchData();
                    }}
                    allCurrencies={allCurrencies}
                />
            )}
        </div>
    );
}

export default App;